import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import type { Artwork } from "@/features/artwork/types";
import { createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  HeartIcon,
  StarIcon,
} from "lucide-react";

export const Route = createFileRoute("/artwork/$artworkId")({
  component: RouteComponent,
  context: () => ({
    features: ["no-layout"],
  }),
});

function RouteComponent() {
  const currentGallery: Artwork = {
    id: "1",
    title: "Sample Artwork",
    content: "This is a sample artwork description.",
    coverUrl: "https://placehold.co/600x400/000/fff",
    imageUrls: [
      "https://placehold.co/600x400/000/fff",
      "https://placehold.co/600x400/111/fff",
      "https://placehold.co/600x400/222/fff",
    ],
    categories: ["Category 1", "Category 2"],
    tags: ["Tag 1", "Tag 2"],
    specialTopic: "Special Topic",
    authorId: "author1",
    author: {
      userId: "author1",
      nickname: "John Doe",
      username: "johndoe",
      avatarUrl: "https://placehold.co/100x100/000/fff",
      bio: "This is a sample bio.",
    },
    createdAt: new Date().toISOString(),
    publishStatus: 1,
    visibility: 1,
    commentAllowance: 1,
  };

  return (
    <div className="flex h-screen bg-graya-app-bg">
      {/* Left side - Image Gallery (70%) */}
      <div className="relative w-full lg:w-[75%] h-full flex items-center justify-center">
        {/* Navigation Arrows */}
        <Button
          size="icon"
          intent="ghost"
          flavor="gray"
          alpha
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-graya-ui/40 rounded-full"
          aria-label="Previous gallery"
        >
          <ChevronLeftIcon />
        </Button>

        {/* Scrollable Image Area */}
        <ScrollArea className="h-full w-full">
          <div className="flex flex-col items-center py-8 px-16 gap-4">
            {currentGallery.imageUrls.map((image) => (
              <div key={image} className="relative w-full max-w-4xl">
                <img
                  src={image}
                  alt="Artwork"
                  width={1200}
                  height={800}
                  className="shadow-lg"
                />
              </div>
            ))}
          </div>
        </ScrollArea>

        <Button
          size="icon"
          intent="ghost"
          flavor="gray"
          alpha
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-graya-ui/40 rounded-full"
          aria-label="Next gallery"
        >
          <ChevronRightIcon />
        </Button>
      </div>

      {/* Right side - Info Panel (30%) */}
      <div className="fixed right-0 top-0 h-full bg-graya-subtle-bg border-l border-graya-border-subtle transition-all duration-300 ease-in-out w-full lg:w-[25%] overflow-hidden">
        <div className="h-full flex flex-col p-6 gap-6">
          {/* <Button
              intent="plain"
              size="icon"
              className="absolute top-4 right-4"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <XIcon />
            </Button> */}

          {/* Publisher Info */}
          <div className="flex items-center gap-2">
            <Avatar className="size-16">
              <AvatarImage
                src={currentGallery.author?.avatarUrl}
                alt={currentGallery.author?.nickname}
              />
              <AvatarFallback>
                {currentGallery.author?.nickname.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-medium text-xl mb-1">
                {currentGallery.author?.nickname}
              </h3>
              <p className="text-sm text-gray-fg-secondary">
                @{currentGallery.author?.username}
              </p>
            </div>
          </div>

          <Separator />

          {/* Work Info */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{currentGallery.title}</h2>
            <p className="pb-2">{currentGallery.content}</p>
            <p className="text-xs text-gray-fg-secondary">
              {new Date(currentGallery.createdAt).toLocaleDateString()}
            </p>
            <div className="flex flex-wrap gap-1 mt-4">
              {currentGallery.tags.map((tag) => (
                <Badge key={tag} variant="subtle" size="md">
                  #{tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Interaction Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex gap-6 text-gray-fg-secondary">
              <div className="flex items-center gap-2">
                <HeartIcon className="size-4" />0
              </div>
              <div className="flex items-center gap-2">
                <StarIcon className="size-4" />0
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                intent="soft"
                flavor="gray"
                alpha
                className="has-[svg]:pl-5"
              >
                <ExternalLinkIcon />
              </Button>
              <Button
                intent="soft"
                flavor="gray"
                alpha
                className="has-[svg]:pl-5"
              >
                <StarIcon />
              </Button>
              <Button intent="soft" flavor="accent" alpha>
                <HeartIcon />
                点赞
              </Button>
            </div>
          </div>

          <Separator />

          {/* Comments Section */}
          {/* <div className="flex-1 overflow-hidden">
              <h3 className="font-medium mb-4">
                Comments ({currentGallery.comments})
              </h3>
              <ScrollArea className="h-[calc(100%-2rem)]">
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>U{i + 1}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">User {i + 1}</span>
                          <span className="text-xs text-muted-foreground">
                            2 days ago
                          </span>
                        </div>
                        <p className="text-sm">
                          This is an amazing collection! I love the composition
                          of image {i + 1}.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div> */}
        </div>
      </div>
    </div>
  );
}
